import { useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

/**
 * Route configuration - maps stages to URL paths
 */
export const ROUTES = {
  landing: '/',
  dashboard: '/dashboard',
  profile: '/profile',
  settings: '/settings',
  pricing: '/pricing',
  goalBuilder: '/create',
  compatibility: '/assessment',
  results: '/assessment/results',
  transition: '/assessment/transition',
  main: '/journey',
  milestoneDetail: '/dream/:dreamId',
  deepDive: '/dream/:dreamId/deep-dive',
  assessment: '/luna-assessment',
  portfolioOverview: '/portfolio',
  lunaOptimization: '/optimize',
  roadmapProfile: '/roadmap/:roadmapId',
  authTest: '/auth-test',
  invite: '/invite/:code', // Dream share invite route
  partnerInvite: '/partner-invite/:code', // Global partnership invite route
};

/**
 * Reverse mapping - URL paths to stages
 */
const PATH_TO_STAGE = {
  '/': 'landing',
  '/dashboard': 'dashboard',
  '/profile': 'profile',
  '/settings': 'settings',
  '/pricing': 'pricing',
  '/create': 'goalBuilder',
  '/assessment': 'compatibility',
  '/assessment/results': 'results',
  '/assessment/transition': 'transition',
  '/journey': 'main',
  '/luna-assessment': 'assessment',
  '/portfolio': 'portfolioOverview',
  '/optimize': 'lunaOptimization',
  '/auth-test': 'authTest',
};

/**
 * Custom hook that syncs URL routing with stage state
 *
 * This allows gradual migration - existing setStage() calls still work,
 * and URLs are kept in sync automatically.
 *
 * @param {string} stage - Current stage state
 * @param {function} setStage - Stage setter function
 * @param {object} options - Additional options
 * @returns {object} Navigation helpers
 */
export const useRouteSync = (stage, setStage, options = {}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    dreamId,
    setDreamId,
    milestoneDetailState,
    setMilestoneDetailState,
  } = options;

  // Track when stage was set by URL change to prevent circular navigation
  const stageSetByUrlRef = useRef(false);
  const lastPathRef = useRef(location.pathname);

  /**
   * Sync URL → Stage (on URL change or initial load)
   */
  useEffect(() => {
    const path = location.pathname;

    // Skip if path hasn't actually changed (prevents unnecessary processing)
    if (path === lastPathRef.current && stage !== 'landing') {
      return;
    }
    lastPathRef.current = path;

    // Check for exact path matches first
    if (PATH_TO_STAGE[path]) {
      const newStage = PATH_TO_STAGE[path];
      if (stage !== newStage) {
        stageSetByUrlRef.current = true; // Mark that URL is setting the stage
        setStage(newStage);
      }
      return;
    }

    // Check for dynamic routes
    // Dream detail: /dream/:dreamId or /dream/:dreamId/:section
    const dreamMatch = path.match(/^\/dream\/([^/]+)(?:\/([^/]+))?$/);
    if (dreamMatch) {
      const [, extractedDreamId, section] = dreamMatch;

      if (section === 'deep-dive') {
        if (stage !== 'deepDive') {
          stageSetByUrlRef.current = true;
          setStage('deepDive');
        }
      } else {
        if (stage !== 'milestoneDetail') {
          stageSetByUrlRef.current = true;
          setStage('milestoneDetail');
        }
        // Update section if provided
        if (section && setMilestoneDetailState) {
          setMilestoneDetailState(prev => ({
            ...prev,
            section: section || 'overview'
          }));
        }
      }

      // Store dreamId for component to use
      if (setDreamId && extractedDreamId !== dreamId) {
        setDreamId(extractedDreamId);
      }
      return;
    }

    // Assessment join with code: /assessment/join/:code
    // NOTE: Don't set stage here - App.js handles this route and extracts the joinCode
    // Setting stage here would cause a race condition where stage is set before code is extracted
    const assessmentJoinMatch = path.match(/^\/assessment\/join\/([^/]+)$/);
    if (assessmentJoinMatch) {
      // Let App.js initializeApp handle this - it extracts the code and sets state together
      return;
    }

    // Roadmap profile: /roadmap/:roadmapId
    const roadmapMatch = path.match(/^\/roadmap\/([^/]+)$/);
    if (roadmapMatch) {
      if (stage !== 'roadmapProfile') {
        stageSetByUrlRef.current = true;
        setStage('roadmapProfile');
      }
      return;
    }

    // Dream share invite: /invite/:code
    const inviteMatch = path.match(/^\/invite\/([^/]+)$/);
    if (inviteMatch) {
      if (stage !== 'invite') {
        stageSetByUrlRef.current = true;
        setStage('invite');
      }
      return;
    }

    // Global partnership invite: /partner-invite/:code
    const partnerInviteMatch = path.match(/^\/partner-invite\/([^/]+)$/);
    if (partnerInviteMatch) {
      if (stage !== 'partnerInvite') {
        stageSetByUrlRef.current = true;
        setStage('partnerInvite');
      }
      return;
    }

    // Default to landing if unknown path
    if (stage !== 'landing' && path !== '/') {
      // Unknown route - could show 404 or redirect
      navigate('/', { replace: true });
    }
  }, [location.pathname]);

  /**
   * Sync Stage → URL (when stage changes via setStage)
   */
  useEffect(() => {
    // If stage was just set by URL change, skip to prevent circular navigation
    if (stageSetByUrlRef.current) {
      stageSetByUrlRef.current = false; // Reset flag
      return;
    }

    const currentPath = location.pathname;
    let targetPath = ROUTES[stage] || '/';

    // CRITICAL: Don't navigate away from valid dynamic routes
    // This prevents race condition where this effect runs before URL→Stage effect updates stage
    const isDynamicRoute = (path) => {
      return (
        path.match(/^\/invite\/[^/]+$/) ||           // /invite/:code
        path.match(/^\/partner-invite\/[^/]+$/) ||   // /partner-invite/:code
        path.match(/^\/roadmap\/[^/]+$/) ||          // /roadmap/:id
        path.match(/^\/dream\/[^/]+/) ||             // /dream/:id or /dream/:id/section
        path.match(/^\/assessment\/join\/[^/]+$/)   // /assessment/join/:code
      );
    };

    // If we're on a dynamic route URL, don't navigate away
    // Let the URL→Stage effect handle setting the correct stage
    if (isDynamicRoute(currentPath)) {
      return;
    }

    // Handle dynamic routes for stage→URL sync
    if (stage === 'milestoneDetail' && dreamId) {
      const section = milestoneDetailState?.section;
      targetPath = section && section !== 'overview'
        ? `/dream/${dreamId}/${section}`
        : `/dream/${dreamId}`;
    } else if (stage === 'deepDive' && dreamId) {
      targetPath = `/dream/${dreamId}/deep-dive`;
    } else if (stage === 'roadmapProfile' && options.selectedRoadmapId) {
      targetPath = `/roadmap/${options.selectedRoadmapId}`;
    }

    // Only navigate if path actually changed
    if (currentPath !== targetPath) {
      navigate(targetPath);
    }
  }, [stage, dreamId, milestoneDetailState?.section]);

  /**
   * Enhanced navigation function that updates both URL and stage
   */
  const navigateTo = useCallback((newStage, params = {}) => {
    const { dreamId: newDreamId, section, replace = false } = params;

    // Update dreamId if provided
    if (newDreamId && setDreamId) {
      setDreamId(newDreamId);
    }

    // Update section if provided
    if (section && setMilestoneDetailState) {
      setMilestoneDetailState(prev => ({ ...prev, section }));
    }

    // Update stage (this will trigger URL sync via useEffect)
    setStage(newStage);
  }, [setStage, setDreamId, setMilestoneDetailState]);

  /**
   * Get shareable URL for current state
   */
  const getShareableUrl = useCallback(() => {
    return window.location.href;
  }, []);

  /**
   * Navigate to dream detail
   */
  const navigateToDream = useCallback((dreamIdParam, section = 'overview') => {
    if (setDreamId) {
      setDreamId(dreamIdParam);
    }
    if (setMilestoneDetailState) {
      setMilestoneDetailState(prev => ({ ...prev, section }));
    }
    setStage('milestoneDetail');
    navigate(`/dream/${dreamIdParam}${section !== 'overview' ? `/${section}` : ''}`);
  }, [navigate, setStage, setDreamId, setMilestoneDetailState]);

  return {
    navigateTo,
    navigateToDream,
    getShareableUrl,
    currentPath: location.pathname,
  };
};

export default useRouteSync;
